import { ParseResult, Schema } from "@effect/schema";
import { Array, Either, identity, pipe, Record } from "effect";
import { toLowerCase } from "effect/String";
import {
  InvalidAddressError,
  Address as ViemAddressType,
  isAddress as isViemAddress,
  getAddress as getViemAddress,
} from "viem";

export interface ViemAddressFromSelf extends Schema.Schema<ViemAddressType> {}

export const ViemAddressFromSelf: ViemAddressFromSelf = Schema.declare(
  (input: unknown): input is ViemAddressType =>
    typeof input === "string" && isViemAddress(input)
).annotations({
  identifier: "ViemAddressFromSelf",
  pretty: () => (address) => `"${address}"`,
});

export function getViemAddressEither(
  value: string
): Either.Either<ViemAddressType, InvalidAddressError> {
  return pipe(
    Either.try({
      try: () => getViemAddress(value),
      catch: (error) => {
        return error instanceof InvalidAddressError
          ? error
          : new InvalidAddressError({ address: value });
      },
    })
  );
}

export const ViemAddress = Schema.transformOrFail(
  Schema.String,
  ViemAddressFromSelf,
  {
    decode: (s, _, ast) => {
      return pipe(
        getViemAddressEither(s),
        Either.match({
          onLeft: (error) =>
            ParseResult.fail(new ParseResult.Type(ast, s, error.message)),
          onRight: (address) => ParseResult.succeed(address),
        })
      );
    },
    encode: (n) => {
      return ParseResult.succeed(n);
    },
  }
).annotations({ identifier: "ViemAddress" });

export type Address = typeof Address.Type;
export type AddressEncoded = typeof Address.Encoded;

export const Address = ViemAddress.pipe(Schema.brand("Address")).annotations({
  identifier: "Address",
});

export const GraphAddress = Schema.transform(Schema.String, Address, {
  decode: identity,
  encode: toLowerCase,
}).annotations({ identifier: "GraphAddress" });

/**
 * Constructs an `Address` from a string.
 *
 * @throws `ParseError` if the string is not a valid address
 */
export function makeAddressSync(value: string): Address {
  return Schema.decodeSync(Address)(value);
}

/**
 * Constructs a record of `Address` values from a record of strings.
 *
 * @throws `ParseError` if any string is not a valid address
 */
export function makeAddressRecordSync<K extends string>(
  record: Record<K, string>
): Record<K, Address> {
  return pipe(record, Record.map(makeAddressSync));
}

/**
 * Constructs an array of `Address` values from an array of strings.
 *
 * @throws `ParseError` if any string is not a valid address
 */
export function makeAddressArraySync(array: string[]): Address[] {
  return pipe(array, Array.map(makeAddressSync));
}

export const ADDRESS_ZERO = makeAddressSync(
  "0x0000000000000000000000000000000000000000"
);

export const isAddress = Schema.is(Address);
